import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { RcButton } from "@/components/RcButton";
import { useSeo } from "@/lib/useSeo";

export default function Auth() {
  useSeo({
    title: "Sign In or Create Account | Park Collections",
    description: "Sign in or create your Park Collections account to save custom dress designs, receive AI previews and place bespoke orders.",
    path: "/auth",
  });
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/designer";

  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") await signup(name, email, password);
      else await login(email, password);
      toast.success(mode === "signup" ? "Welcome to the atelier." : "Welcome back.");
      navigate(redirectTo);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1654764746225-e63f5e90facd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBicmlkYWwlMjBsZWhlbmdhJTIwZW1icm9pZGVyeXxlbnwwfHx8fDE3ODI0NDMxNzB8MA&ixlib=rb-4.1.0&q=85"
          alt="Atelier"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#120D0C]/80" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-4">Park Collections</div>
          <div className="font-display text-4xl leading-tight max-w-md">
            &ldquo;A garment unmade until you imagine it.&rdquo;
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16 bg-[#1F1816]">
        <div className="w-full max-w-md rc-enter">
          <div className="flex items-center gap-6 mb-10">
            <button
              data-testid="tab-login"
              onClick={() => setMode("login")}
              className={`text-xs tracking-[0.3em] uppercase pb-2 border-b transition ${
                mode === "login" ? "text-[#CFA05E] border-[#CFA05E]" : "text-[#A39E93] border-transparent"
              }`}
            >
              Sign In
            </button>
            <button
              data-testid="tab-signup"
              onClick={() => setMode("signup")}
              className={`text-xs tracking-[0.3em] uppercase pb-2 border-b transition ${
                mode === "signup" ? "text-[#CFA05E] border-[#CFA05E]" : "text-[#A39E93] border-transparent"
              }`}
            >
              Create Account
            </button>
          </div>

          <h1 className="sr-only">Sign in or create your Park Collections account</h1>
          <h2 className="font-display text-4xl mb-2">
            {mode === "signup" ? "Begin your bespoke journey." : "Welcome back."}
          </h2>
          <p className="text-sm text-[#A39E93] mb-10 font-light">
            {mode === "signup"
              ? "An account to save your designs and orders."
              : "Sign in to access your designs and orders."}
          </p>

          <form onSubmit={submit} className="space-y-5">
            {mode === "signup" && (
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#A39E93] block mb-2">Name</label>
                <input
                  data-testid="signup-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] focus:ring-1 focus:ring-[#CFA05E] outline-none px-4 py-3 text-sm transition"
                />
              </div>
            )}
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#A39E93] block mb-2">Email</label>
              <input
                data-testid="auth-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] focus:ring-1 focus:ring-[#CFA05E] outline-none px-4 py-3 text-sm transition"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#A39E93] block mb-2">Password</label>
              <input
                data-testid="auth-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] focus:ring-1 focus:ring-[#CFA05E] outline-none px-4 py-3 text-sm transition"
              />
            </div>

            <RcButton
              type="submit"
              data-testid="auth-submit"
              disabled={loading}
              className="w-full mt-4"
            >
              {loading ? "Please wait…" : mode === "signup" ? "Create Account" : "Sign In"}
            </RcButton>
          </form>

          <p className="text-xs text-[#A39E93] mt-8 font-light leading-relaxed">
            Bespoke garments, tailored to you. Have questions? Reach us at <span className="text-[#CFA05E]">care@parkcollections.co.in</span>.
          </p>
        </div>
      </div>
    </div>
  );
}
